import React, { useEffect, useState } from "react";
import { Card, CardHeader, Button, Collapse, CardBody, FormGroup, Label, Input, FormFeedback } from "reactstrap";
import EnumInput from "../../../../../misc/EnumInput";
import { useTranslation } from 'react-i18next';
import { CoreEduCloudConfiguration, EduCloudActivityNaming, EduCloudActivityNamingOptions, EduCloudIncludeSensitiveFieldsOptions, EduCloudSchoolOrganizationType, EduCloudSchoolOrganizationTypeOptions } from "../../../../../../models/CoreEduCloudModels";
import i18n from 'i18next';


export const validator = (configuration: CoreEduCloudConfiguration, active: boolean) => {
    const validation = [];

    if (active) {
        if (!configuration.customerId || configuration.customerId === "") {
            validation.push('customerId');
        }
        if (!configuration.clientId || configuration.clientId === "") {
            validation.push('clientId');
        }
        if (!configuration.clientSecret || configuration.clientSecret === "") {
            validation.push('clientSecret');
        }
        if (configuration.schoolOrganizationType !== EduCloudSchoolOrganizationType.School
            && configuration.schoolOrganizationType !== EduCloudSchoolOrganizationType.SchoolUnit) {
            validation.push('schoolOrganizationType');
        }
        if (configuration.activityNaming !== EduCloudActivityNaming.ActivityDisplayName
            && configuration.activityNaming !== EduCloudActivityNaming.GroupDisplayName) {
            validation.push('activityNaming');
        }
    }

    if (configuration.municipalityCode === "") {
        validation.push('municipalityCode')
    }

    return validation;
}

export const warningMessages = (originalConfiguration: CoreEduCloudConfiguration, updatedConfiguration: CoreEduCloudConfiguration) => {
    const warnings: string[] = [];

    if (updatedConfiguration.includeSensitiveFields !== undefined && (updatedConfiguration.includeSensitiveFields & 8) !== 8 && (originalConfiguration.includeSensitiveFields & 8) === 8) {
        warnings.push(i18n.t('eduCloudConfiguration.includeSensitiveFields.warningMessage', { ns: 'configuration' }));
    }

    return warnings;
}


interface EduCloudConfigurationProps {
    configuration: CoreEduCloudConfiguration
    disabled: boolean
    updatePart: (part: Partial<CoreEduCloudConfiguration>) => void
    active: boolean
    onValidChange: (arg: boolean) => void 
}

const EduCloudConfiguration = (props: EduCloudConfigurationProps) => {
    const { t } = useTranslation(['configuration', 'common']);

    const [open, setOpen] = useState<boolean>(true);
    const [onlyWithMunicipalityCode, setOnlyWithMunicipalityCode] = useState<boolean>(props.configuration.municipalityCode ? true : false)

    const validation = validator(props.configuration, props.active);

    useEffect(() => {
        props.onValidChange(validation.length ? true : false)
    }, [props.configuration, props.active]);

    const updateEduCloudConfiguration = (prop: string, value: boolean) => {
        if (prop === 'onlyWithMunicipalityCode') {
            setOnlyWithMunicipalityCode(value);
        }
        if (prop === 'onlyWithMunicipalityCode' && value === true) {
            props.updatePart({ municipalityCode: '' })
        }
        if (prop === 'onlyWithMunicipalityCode' && value === false) {
            props.updatePart({ municipalityCode: undefined })
        }
    }

    return (
        <Card className="edit_module">
            <CardHeader tag="h2">
                <Button outline color="secondary" style={{ width: '37px', float: 'right' }} onClick={() => setOpen(!open)}>{open ? '-' : '+'}</Button>
                {t('modules.EduCloud.title', { ns: 'common' })}
            </CardHeader>
            <Collapse isOpen={open}>
                <CardBody>
                    <div style={{ margin: '1rem 0' }}>
                        <h5>{t('eduCloudConfiguration.access', {ns: 'configuration'})}</h5>
                        <FormGroup>
                            <Label for="customerId">{t('eduCloudConfiguration.customerId.title', { ns: 'configuration' })}</Label>
                            <Input
                                type="text"
                                name="customerId"
                                id="customerId"
                                placeholder={t('eduCloudConfiguration.customerId.title', { ns: 'configuration' })}
                                value={props.configuration.customerId || ''}
                                disabled={props.disabled}
                                onChange={(e) => props.updatePart({ customerId: e.target.value })}
                                invalid={validation.indexOf('customerId') !== -1}
                                autoComplete="off"
                            />
                            <FormFeedback>{t('eduCloudConfiguration.customerId.formFeedback', { ns: 'configuration' })}</FormFeedback>
                        </FormGroup>
                        <FormGroup>
                            <Label for="clientId">{t('eduCloudConfiguration.clientId.title', { ns: 'configuration' })}</Label>
                            <Input
                                type="text"
                                name="clientId"
                                id="clientId"
                                placeholder={t('eduCloudConfiguration.clientId.title', { ns: 'configuration' })}
                                value={props.configuration.clientId || ''}
                                disabled={props.disabled}
                                onChange={(e) => props.updatePart({ clientId: e.target.value })}
                                invalid={validation.indexOf('clientId') !== -1}
                                autoComplete="off"
                            />
                            <FormFeedback>{t('eduCloudConfiguration.clientId.formFeedback', { ns: 'configuration' })}</FormFeedback>
                        </FormGroup>
                        <FormGroup>
                            <Label for="clientSecret">{t('eduCloudConfiguration.clientSecret.title', { ns: 'configuration' })}</Label>
                            <Input
                                type="text"
                                name="clientSecret"
                                id="clientSecret"
                                placeholder={t('eduCloudConfiguration.clientSecret.title', { ns: 'configuration' })}
                                value={props.configuration.clientSecret || ''}
                                disabled={props.disabled}
                                onChange={(e) => props.updatePart({ clientSecret: e.target.value })}
                                invalid={validation.indexOf('clientSecret') !== -1}
                                autoComplete="off"
                            />
                            <FormFeedback>{t('eduCloudConfiguration.clientSecret.formFeedback', { ns: 'configuration' })}</FormFeedback>
                        </FormGroup>

                        <FormGroup>
                            <h5>{t('eduCloudConfiguration.onlyWithMunicipalityCode.title', { ns: 'configuration' })}</h5>
                            <FormGroup check name="onlyWithMunicipalityCode">
                                <div>
                                    <Label check style={{ marginBottom: '8px' }}>
                                        <Input
                                            type="checkbox"
                                            name="onlyWithMunicipalityCode"
                                            id="onlyWithMunicipalityCode"
                                            disabled={props.disabled}
                                            checked={onlyWithMunicipalityCode}
                                            onChange={(e) => updateEduCloudConfiguration('onlyWithMunicipalityCode', e.target.checked)}
                                        />
                                        {t('eduCloudConfiguration.onlyWithMunicipalityCode.label', { ns: 'configuration' })}
                                    </Label>
                                </div>
                                <span>
                                    <Input
                                        invalid={validation.indexOf('municipalityCode') !== -1}
                                        type="text"
                                        disabled={props.disabled || !onlyWithMunicipalityCode}
                                        placeholder={t('eduCloudConfiguration.onlyWithMunicipalityCode.placeholder', { ns: 'configuration' })}
                                        name="municipalityCode"
                                        id="municipalityCode"
                                        value={props.configuration.municipalityCode || ''}
                                        onChange={(e) => props.updatePart({ municipalityCode: e.target.value })} 
                                    />
                                    <FormFeedback>{t('eduCloudConfiguration.onlyWithMunicipalityCode.formFeedback', { ns: 'configuration' })}</FormFeedback>
                                </span>
                            </FormGroup>
                        </FormGroup>

                        <EnumInput
                            title={t('eduCloudConfiguration.includeSensitiveFields.title', { ns: 'configuration' })}
                            value={props.configuration.includeSensitiveFields}
                            options={EduCloudIncludeSensitiveFieldsOptions
                                .map(key => ({ value: key, text: t(`eduCloudConfiguration.includeSensitiveFields.options.${key}`, { ns: 'configuration' }) }))}
                            onChange={(name, value) => props.updatePart({ [name]: value })}
                            disabled={props.disabled}
                            name='includeSensitiveFields'
                            multi={true}
                        />

                        <EnumInput
                            title={t('eduCloudConfiguration.schoolOrganizationType.title', { ns: 'configuration' })}
                            value={props.configuration.schoolOrganizationType}
                            options={EduCloudSchoolOrganizationTypeOptions.map(x => ({
                                value: x,
                                text: t(`eduCloudConfiguration.schoolOrganizationType.options.${x}`, { ns: 'configuration' }),
                            }))}
                            onChange={(name, value) => props.updatePart({ [name]: value })}
                            disabled={props.disabled}
                            name='schoolOrganizationType'
                            invalid={validation.indexOf('schoolOrganizationType') !== -1}
                            invalidFeedback={t('eduCloudConfiguration.schoolOrganizationType.formFeedback', { ns: 'configuration' })}
                        />

                        <EnumInput
                            title={t('eduCloudConfiguration.activityNaming.title', { ns: 'configuration' })}
                            value={props.configuration.activityNaming}
                            options={EduCloudActivityNamingOptions.map(x => ({
                                value: x,
                                text: t(`eduCloudConfiguration.activityNaming.options.${x}`, { ns: 'configuration' }),
                            }))}
                            onChange={(name, value) => props.updatePart({ [name]: value })}
                            disabled={props.disabled}
                            name='activityNaming'
                            invalid={validation.indexOf('activityNaming') !== -1}
                            invalidFeedback={t('eduCloudConfiguration.activityNaming.formFeedback', { ns: 'configuration' })}
                        />

                    </div>
                </CardBody>
            </Collapse>
        </Card>)
}

export default EduCloudConfiguration;
